import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { FiArrowRight, FiMenu, FiX } from "react-icons/fi";
import eazyPaisaLogo from "../assets/eazypaisa.webp";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Process", href: "/how-it-works" },
  { label: "Contact", href: "/contact" },
  { label: "Blog", href: "/blogs" },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const activePath = location.pathname;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [activePath]);

  const isActive = (href: string) =>
    href === "/" ? activePath === "/" : activePath.startsWith(href);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 bg-ep-cream transition-shadow ${
        scrolled || menuOpen ? "shadow-[0_1px_0_#D3D8D0]" : ""
      }`}
    >
      <nav className="page-container flex h-[88px] items-center xl:h-[104px]">
        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center" aria-label="EazyPaisa home">
          <img
            className="h-20 w-auto object-contain xl:h-24"
            src={eazyPaisaLogo}
            alt="EazyPaisa"
            width={595}
            height={216}
            fetchPriority="high"
          />
        </Link>

        {/* Desktop links */}
        <ul className="ml-auto hidden items-center gap-8 xl:flex">
          {NAV_LINKS.map((item) => (
            <li key={item.label}>
              <Link
                to={item.href}
                aria-current={isActive(item.href) ? "page" : undefined}
                className={`whitespace-nowrap text-[15px] text-ep-green transition-opacity hover:opacity-70 ${
                  isActive(item.href) ? "font-semibold" : "font-normal"
                }`}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Desktop CTAs */}
        <div className="ml-28 hidden items-center gap-4 xl:flex">
          <Link
            to="/repay"
            className="mr-4 whitespace-nowrap text-[15px] text-ep-green underline underline-offset-4 transition-opacity hover:opacity-70"
          >
            Repay Now
          </Link>
          <Link
            to="/login"
            className="inline-flex h-10 items-center rounded-md border-[1.5px] border-ep-green px-6 text-[15px] font-semibold text-ep-green transition-colors hover:bg-ep-green hover:text-ep-ivory"
          >
            Login
          </Link>
          <a
            href={import.meta.env.VITE_WEBSITE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-10 items-center gap-3 rounded-md bg-ep-green px-6 text-[15px] font-semibold text-ep-ivory transition-colors hover:bg-ep-green-dark"
          >
            Apply Now
            <FiArrowRight aria-hidden className="text-base" />
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="ml-auto -mr-2 p-2 text-2xl text-ep-green xl:hidden"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
        >
          {menuOpen ? <FiX /> : <FiMenu />}
        </button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div id="mobile-menu" className="border-t border-ep-line bg-ep-cream xl:hidden">
          <div className="page-container flex flex-col py-4">
            {NAV_LINKS.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                aria-current={isActive(item.href) ? "page" : undefined}
                className={`border-b border-ep-line py-3.5 text-base text-ep-green ${
                  isActive(item.href) ? "font-semibold" : ""
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link to="/repay" className="border-b border-ep-line py-3.5 text-base text-ep-green underline underline-offset-4">
              Repay Now
            </Link>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <Link
                to="/login"
                className="inline-flex h-11 items-center justify-center rounded-md border-[1.5px] border-ep-green text-[15px] font-semibold text-ep-green"
              >
                Login
              </Link>
              <a
                href={import.meta.env.VITE_WEBSITE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-ep-green text-[15px] font-semibold text-ep-ivory"
              >
                Apply Now
                <FiArrowRight aria-hidden />
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
